import React, { useState } from 'react';
import { Globe, Server, RefreshCw, Play, AlertCircle, CheckCircle, Clock, ChevronDown, ChevronUp, SkipForward, Shield, Calendar, Cloud, Copy, Check } from 'lucide-react';

interface StepResult {
  success: boolean;
  data?: any;
  error?: string;
  skipped?: boolean;
  debugInfo?: {
    status?: number;
    statusText?: string;
    responseData?: any;
  };
}

interface NameserverResult {
  success: boolean;
  domain: string;
  registrar?: string;
  nameservers?: string[];
  message?: string;
  error?: string;
  skipped?: boolean;
  notOwned?: boolean;
  expired?: boolean;
  notInCloudflare?: boolean;
  unregistered?: boolean;
  steps?: {
    checkRegistrar?: StepResult;
    getNameservers?: StepResult;
    updateNameservers?: StepResult;
  };
}

interface ApiResponse {
  success: boolean;
  message: string;
  summary: {
    total: number;
    successful: number;
    failed: number;
    skipped?: number;
    notOwned?: number;
    expired?: number;
    notInCloudflare?: number;
    unregistered?: number;
  };
  results: NameserverResult[];
}

interface ProcessingStatus {
  domain: string;
  status: 'pending' | 'checking-registrar' | 'getting-nameservers' | 'updating-nameservers' | 'completed' | 'failed' | 'skipped' | 'not-owned' | 'expired' | 'not-in-cf' | 'unregistered';
  currentStep?: string;
  steps?: {
    checkRegistrar?: StepResult;
    getNameservers?: StepResult;
    updateNameservers?: StepResult;
  };
  result?: NameserverResult;
}

export const Nameservers: React.FC = () => {
  const [domainsInput, setDomainsInput] = useState('');
  const [apiKeys, setApiKeys] = useState({
    dynadot: '',
    porkbun: { apiKey: '', secretKey: '' },
    spaceship: { apiKey: '', apiSecret: '' }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ApiResponse | null>(null);
  const [liveProcessing, setLiveProcessing] = useState<ProcessingStatus[]>([]);
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(new Set());
  const [expandedSummaryCategories, setExpandedSummaryCategories] = useState<Set<string>>(new Set());
  const [copiedCategory, setCopiedCategory] = useState<string | null>(null);

  const getDomainsList = () => {
    return domainsInput
      .split('\n')
      .map(domain => domain.trim())
      .filter(domain => domain.length > 0);
  };

  const toggleDetails = (domain: string) => {
    const newExpanded = new Set(expandedDetails);
    if (newExpanded.has(domain)) {
      newExpanded.delete(domain);
    } else {
      newExpanded.add(domain);
    }
    setExpandedDetails(newExpanded);
  };

  const toggleSummaryCategory = (category: string) => {
    const newExpanded = new Set(expandedSummaryCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedSummaryCategories(newExpanded);
  };

  const copyToClipboard = async (category: string) => {
    const domains = getDomainsByCategory(category);
    const domainList = domains.map(result => result.domain).join('\n');
    
    try {
      await navigator.clipboard.writeText(domainList);
      setCopiedCategory(category);
      setTimeout(() => setCopiedCategory(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const getDomainsByCategory = (category: string) => {
    if (!results) return [];
    
    switch (category) {
      case 'successful':
        return results.results.filter(r => r.success && !r.skipped);
      case 'skipped':
        return results.results.filter(r => r.skipped);
      case 'not-owned':
        return results.results.filter(r => r.notOwned);
      case 'expired':
        return results.results.filter(r => r.expired);
      case 'not-in-cf':
        return results.results.filter(r => r.notInCloudflare);
      case 'unregistered':
        return results.results.filter(r => r.unregistered);
      case 'failed':
        return results.results.filter(r => !r.success && !r.expired && !r.notOwned && !r.notInCloudflare && !r.skipped && !r.unregistered);
      default:
        return [];
    }
  };

  const handleUpdateNameservers = async () => {
    const domains = getDomainsList();
    
    if (domains.length === 0) {
      alert('Please enter at least one domain');
      return;
    }

    setIsProcessing(true);
    setResults(null);
    setLiveProcessing([]);
    setExpandedDetails(new Set());

    // Initialize processing status for each domain
    const initialProcessing = domains.map(domain => ({
      domain,
      status: 'pending' as const,
      steps: {}
    }));
    setLiveProcessing(initialProcessing);

    try {
      // Process domains one by one to show live updates
      const allResults: NameserverResult[] = [];
      
      for (let i = 0; i < domains.length; i++) {
        const domain = domains[i].trim();
        if (!domain) continue;

        // Update status to processing
        setLiveProcessing(prev => prev.map(item => 
          item.domain === domain 
            ? { ...item, status: 'checking-registrar', currentStep: 'Checking registrar...' }
            : item
        ));

        try {
          // Make API call for single domain
          const response = await fetch('/api/domains/update-nameservers', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': process.env.REACT_APP_API_KEY || ''
            },
            body: JSON.stringify({
              domains: [domain],
              apiKeys: apiKeys
            })
          });

          if (response.status === 401) {
            throw new Error('API authentication failed. Please check if REACT_APP_API_KEY is configured correctly.');
          }

          const data = await response.json();
          
          if (response.ok && data.results && data.results.length > 0) {
            const result = data.results[0];
            allResults.push(result);
            
            // Update processing status with final result
            setLiveProcessing(prev => prev.map(item => 
              item.domain === domain 
                ? { 
                    ...item, 
                    status: result.unregistered ? 'unregistered' : result.notInCloudflare ? 'not-in-cf' : result.expired ? 'expired' : result.notOwned ? 'not-owned' : result.skipped ? 'skipped' : result.success ? 'completed' : 'failed',
                    currentStep: result.unregistered
                      ? 'Domain is unregistered'
                      : result.notInCloudflare
                        ? 'Domain not found in Cloudflare'
                        : result.expired
                          ? 'Domain has expired'
                          : result.notOwned 
                            ? 'Domain not owned by Alphoric'
                            : result.skipped 
                              ? 'Domain already active - skipped update' 
                              : result.success 
                                ? 'Completed successfully' 
                                : 'Failed',
                    steps: result.steps || {},
                    result: result
                  }
                : item
            ));
            
            // Expand details for completed domain
            setExpandedDetails(prev => new Set([...Array.from(prev), domain]));
          } else {
            // Handle error case
            const errorResult: NameserverResult = {
              success: false,
              domain: domain,
              error: data.message || 'Unknown error occurred'
            };
            allResults.push(errorResult);
            
            setLiveProcessing(prev => prev.map(item => 
              item.domain === domain 
                ? { 
                    ...item, 
                    status: 'failed',
                    currentStep: 'Failed to process',
                    result: errorResult
                  }
                : item
            ));
          }
        } catch (error) {
          console.error(`Error processing domain ${domain}:`, error);
          const errorResult: NameserverResult = {
            success: false,
            domain: domain,
            error: 'Network error occurred'
          };
          allResults.push(errorResult);
          
          setLiveProcessing(prev => prev.map(item => 
            item.domain === domain 
              ? { 
                  ...item, 
                  status: 'failed',
                  currentStep: 'Network error',
                  result: errorResult
                }
              : item
          ));
        }

        // Small delay between domains to make processing visible
        if (i < domains.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Calculate final summary
      const successful = allResults.filter(r => r.success).length;
      const failed = allResults.filter(r => !r.success && !r.expired && !r.notOwned && !r.notInCloudflare && !r.unregistered).length;
      const skipped = allResults.filter(r => r.skipped).length;
      const notOwned = allResults.filter(r => r.notOwned).length;
      const expired = allResults.filter(r => r.expired).length;
      const notInCloudflare = allResults.filter(r => r.notInCloudflare).length;
      const unregistered = allResults.filter(r => r.unregistered).length;

      setResults({
        success: true,
        message: `Processed ${allResults.length} domains: ${successful} successful, ${skipped} skipped, ${notOwned} not owned, ${expired} expired, ${unregistered} unregistered, ${notInCloudflare} not in CF, ${failed} failed`,
        summary: {
          total: allResults.length,
          successful: successful,
          failed: failed,
          skipped: skipped,
          notOwned: notOwned,
          expired: expired,
          notInCloudflare: notInCloudflare,
          unregistered: unregistered
        },
        results: allResults
      });

    } catch (error) {
      console.error('Error updating nameservers:', error);
      alert('Failed to connect to the API. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusIcon = (status: ProcessingStatus['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-gray-400" />;
      case 'checking-registrar':
      case 'getting-nameservers':
      case 'updating-nameservers':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'skipped':
        return <SkipForward className="h-4 w-4 text-gray-400" />;
      case 'not-owned':
        return <Shield className="h-4 w-4 text-orange-500" />;
      case 'expired':
        return <Calendar className="h-4 w-4 text-purple-500" />;
      case 'not-in-cf':
        return <Cloud className="h-4 w-4 text-indigo-500" />;
      case 'unregistered':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: ProcessingStatus['status']) => {
    switch (status) {
      case 'pending':
        return 'text-gray-500';
      case 'checking-registrar':
      case 'getting-nameservers':
      case 'updating-nameservers':
        return 'text-blue-600';
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'skipped':
        return 'text-gray-500';
      case 'not-owned':
        return 'text-orange-600';
      case 'expired':
        return 'text-purple-600';
      case 'not-in-cf':
        return 'text-indigo-600';
      case 'unregistered':
        return 'text-yellow-600';
      default:
        return 'text-gray-500';
    }
  };

  const renderStepDetails = (stepName: string, stepData: StepResult) => {
    const getStepStatusStyle = () => {
      if (stepData.skipped) {
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      } else if (stepData.success) {
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      } else {
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
      }
    };

    const getStepStatusText = () => {
      if (stepData.skipped) {
        return 'Skipped';
      } else if (stepData.success) {
        return 'Success';
      } else {
        return 'Failed';
      }
    };

    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <h5 className="font-medium text-sm">{stepName}</h5>
          <span className={`text-xs px-2 py-1 rounded-full ${getStepStatusStyle()}`}>
            {getStepStatusText()}
          </span>
        </div>
        
        {stepData.error && (
          <div className="text-xs text-red-600 dark:text-red-400">
            <strong>Error:</strong> {stepData.error}
            {stepData.debugInfo && (
              <div className="mt-2">
                <strong>Debug Info:</strong>
                <div className="mt-1 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                  <p><strong>Status:</strong> {stepData.debugInfo.status} {stepData.debugInfo.statusText}</p>
                  {stepData.debugInfo.responseData && (
                    <div className="mt-1">
                      <strong>Response:</strong>
                      <pre className="mt-1 p-2 bg-red-100 dark:bg-red-800/30 rounded text-xs overflow-x-auto">
                        {JSON.stringify(stepData.debugInfo.responseData, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {stepData.skipped && (
          <div className="text-xs text-blue-600 dark:text-blue-400">
            <strong>Note:</strong> {stepData.data?.message || 'Step was skipped'}
          </div>
        )}
        
        {stepData.data && !stepData.skipped && (
          <div className="text-xs">
            <strong>Response:</strong>
            <pre className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs overflow-x-auto">
              {JSON.stringify(stepData.data, null, 2)}
            </pre>
          </div>
        )}

        {stepData.data && stepData.skipped && (
          <div className="text-xs">
            <strong>Status:</strong>
            <pre className="mt-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs overflow-x-auto">
              {JSON.stringify(stepData.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Update Nameservers at Registrar</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Automatically check registrar and update nameservers for multiple domains with live processing view. Skips update if domain is already active.
        </p>
      </div>

      <div className="card-modern p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">Domain List</h2>
          <div>
            <label className="text-sm font-medium mb-2 block">
              Domain Names (one per line)
            </label>
            <textarea
              value={domainsInput}
              onChange={(e) => setDomainsInput(e.target.value)}
              className="input-modern w-full px-3 py-2 text-sm min-h-[120px] resize-y"
              placeholder="example1.com&#10;example2.com&#10;example3.com"
              rows={6}
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {getDomainsList().length} domain{getDomainsList().length !== 1 ? 's' : ''} entered
            </p>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h2 className="text-lg font-semibold mb-4">API Keys (Optional)</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                DYNADOT API Key
              </label>
              <input
                type="password"
                value={apiKeys.dynadot}
                onChange={(e) => setApiKeys(prev => ({ ...prev, dynadot: e.target.value }))}
                className="input-modern h-10 w-full px-3 text-sm"
                placeholder="Enter DYNADOT API key"
                disabled={isProcessing}
              />
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium mb-2 block">
                  Porkbun API Key
              </label>
                <input
                  type="password"
                  value={apiKeys.porkbun.apiKey}
                  onChange={(e) => setApiKeys(prev => ({ 
                    ...prev, 
                    porkbun: { ...prev.porkbun, apiKey: e.target.value }
                  }))}
                className="input-modern h-10 w-full px-3 text-sm"
                  placeholder="Enter Porkbun API key"
                  disabled={isProcessing}
                />
            </div>
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Porkbun Secret Key
                </label>
                <input
                  type="password"
                  value={apiKeys.porkbun.secretKey}
                  onChange={(e) => setApiKeys(prev => ({ 
                    ...prev, 
                    porkbun: { ...prev.porkbun, secretKey: e.target.value }
                  }))}
                  className="input-modern h-10 w-full px-3 text-sm"
                  placeholder="Enter Porkbun secret key"
                  disabled={isProcessing}
                />
          </div>
        </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Spaceship API Key
                </label>
                  <input
                  type="password"
                  value={apiKeys.spaceship.apiKey}
                  onChange={(e) => setApiKeys(prev => ({ 
                    ...prev, 
                    spaceship: { ...prev.spaceship, apiKey: e.target.value }
                  }))}
                  className="input-modern h-10 w-full px-3 text-sm"
                  placeholder="Enter Spaceship API key"
                  disabled={isProcessing}
                  />
                </div>
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Spaceship API Secret
                </label>
                <input
                  type="password"
                  value={apiKeys.spaceship.apiSecret}
                  onChange={(e) => setApiKeys(prev => ({ 
                    ...prev, 
                    spaceship: { ...prev.spaceship, apiSecret: e.target.value }
                  }))}
                  className="input-modern h-10 w-full px-3 text-sm"
                  placeholder="Enter Spaceship API secret"
                  disabled={isProcessing}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end pt-6">
            <button 
              onClick={handleUpdateNameservers}
              disabled={isProcessing || getDomainsList().length === 0}
              className="btn-primary px-6 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
              <span>Update Nameservers</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Live Processing Section */}
      {liveProcessing.length > 0 && (
        <div className="card-modern p-6">
          <h2 className="text-lg font-semibold mb-4">Live Processing</h2>
          <div className="space-y-3">
            {liveProcessing.map((item, index) => (
              <div key={index} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(item.status)}
                    <div>
                      <h3 className="font-medium">{item.domain}</h3>
                      <p className={`text-sm ${getStatusColor(item.status)}`}>
                        {item.currentStep || item.status}
                      </p>
                      {item.result?.registrar && (
                        <p className="text-xs text-muted-foreground">
                          Registrar: {item.result.registrar}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {item.steps && Object.keys(item.steps).length > 0 && (
                    <button
                      onClick={() => toggleDetails(item.domain)}
                      className="text-sm text-primary hover:text-primary/80 flex items-center space-x-1"
                    >
                      <span>Details</span>
                      {expandedDetails.has(item.domain) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>

                {/* Expanded Details */}
                {expandedDetails.has(item.domain) && item.steps && (
                  <div className="mt-4 space-y-3">
                    {item.steps.checkRegistrar && (
                      renderStepDetails('1. Check Registrar', item.steps.checkRegistrar)
                    )}
                    {item.steps.getNameservers && (
                      renderStepDetails('2. Get Nameservers', item.steps.getNameservers)
                    )}
                    {item.steps.updateNameservers && (
                      renderStepDetails('3. Update Nameservers', item.steps.updateNameservers)
                    )}
                  </div>
                )}

                {/* Show nameservers if available */}
                {item.result?.nameservers && item.result.nameservers.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Nameservers:</p>
                    <div className="flex flex-wrap gap-2">
                      {item.result.nameservers.map((ns, nsIndex) => (
                        <div key={nsIndex} className="flex items-center space-x-1">
                          <Server className="h-3 w-3 text-muted-foreground" />
                          <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{ns}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Final Results Section */}
      {results && (
        <div className="space-y-4">
          <div className="card-modern p-6">
            <h2 className="text-lg font-semibold mb-4">Final Results</h2>
            
            {/* Summary */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <span className="text-sm font-medium text-blue-600">Total Processed</span>
                <span className="text-xl font-bold text-blue-600">{results.summary.total}</span>
              </div>
              
              <div 
                className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/20 rounded-lg cursor-pointer hover:bg-green-500/20 transition-colors"
                onClick={() => toggleSummaryCategory('successful')}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-green-600">Successful</span>
                  {expandedSummaryCategories.has('successful') ? (
                    <ChevronUp className="h-4 w-4 text-green-600" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-green-600" />
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard('successful');
                    }}
                    className="p-1 hover:bg-green-600/20 rounded transition-colors"
                    title="Copy domains to clipboard"
                  >
                    {copiedCategory === 'successful' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 text-green-600" />
                    )}
                  </button>
                  <span className="text-xl font-bold text-green-600">{results.summary.successful}</span>
                </div>
              </div>
              {expandedSummaryCategories.has('successful') && results.summary.successful > 0 && (
                <div className="ml-4 space-y-2">
                  {getDomainsByCategory('successful').map((result, index) => (
                    <div key={index} className="flex items-center space-x-3 p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">{result.domain}</span>
                      {result.registrar && (
                        <span className="text-xs text-green-600 bg-green-100 dark:bg-green-800 px-2 py-1 rounded">
                          {result.registrar}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {(results.summary.skipped ?? 0) > 0 && (
                <>
                  <div 
                    className="flex items-center justify-between p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg cursor-pointer hover:bg-gray-500/20 transition-colors"
                    onClick={() => toggleSummaryCategory('skipped')}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-600">Skipped (Already Active)</span>
                      {expandedSummaryCategories.has('skipped') ? (
                        <ChevronUp className="h-4 w-4 text-gray-600" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-600" />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard('skipped');
                        }}
                        className="p-1 hover:bg-gray-600/20 rounded transition-colors"
                        title="Copy domains to clipboard"
                      >
                        {copiedCategory === 'skipped' ? (
                          <Check className="h-4 w-4 text-gray-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                      <span className="text-xl font-bold text-gray-600">{results.summary.skipped}</span>
                    </div>
                  </div>
                  {expandedSummaryCategories.has('skipped') && (
                    <div className="ml-4 space-y-2">
                      {getDomainsByCategory('skipped').map((result, index) => (
                        <div key={index} className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-900/20 rounded">
                          <SkipForward className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium">{result.domain}</span>
                          {result.registrar && (
                            <span className="text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                              {result.registrar}
                            </span>
                          )}
                          <span className="text-xs text-gray-500">Already active</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {(results.summary.notOwned ?? 0) > 0 && (
                <>
                  <div 
                    className="flex items-center justify-between p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg cursor-pointer hover:bg-orange-500/20 transition-colors"
                    onClick={() => toggleSummaryCategory('not-owned')}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-orange-600">Not Owned by Alphoric</span>
                      {expandedSummaryCategories.has('not-owned') ? (
                        <ChevronUp className="h-4 w-4 text-orange-600" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-orange-600" />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard('not-owned');
                        }}
                        className="p-1 hover:bg-orange-600/20 rounded transition-colors"
                        title="Copy domains to clipboard"
                      >
                        {copiedCategory === 'not-owned' ? (
                          <Check className="h-4 w-4 text-orange-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-orange-600" />
                        )}
                      </button>
                      <span className="text-xl font-bold text-orange-600">{results.summary.notOwned}</span>
                    </div>
                  </div>
                  {expandedSummaryCategories.has('not-owned') && (
                    <div className="ml-4 space-y-2">
                      {getDomainsByCategory('not-owned').map((result, index) => (
                        <div key={index} className="flex items-center space-x-3 p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                          <Shield className="h-4 w-4 text-orange-500" />
                          <span className="text-sm font-medium">{result.domain}</span>
                          <span className="text-xs text-orange-600 bg-orange-100 dark:bg-orange-800 px-2 py-1 rounded">
                            Not owned by Alphoric
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {(results.summary.expired ?? 0) > 0 && (
                <>
                  <div 
                    className="flex items-center justify-between p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg cursor-pointer hover:bg-purple-500/20 transition-colors"
                    onClick={() => toggleSummaryCategory('expired')}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-purple-600">Expired Domains</span>
                      {expandedSummaryCategories.has('expired') ? (
                        <ChevronUp className="h-4 w-4 text-purple-600" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-purple-600" />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard('expired');
                        }}
                        className="p-1 hover:bg-purple-600/20 rounded transition-colors"
                        title="Copy domains to clipboard"
                      >
                        {copiedCategory === 'expired' ? (
                          <Check className="h-4 w-4 text-purple-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-purple-600" />
                        )}
                      </button>
                      <span className="text-xl font-bold text-purple-600">{results.summary.expired}</span>
                    </div>
                  </div>
                  {expandedSummaryCategories.has('expired') && (
                    <div className="ml-4 space-y-2">
                      {getDomainsByCategory('expired').map((result, index) => (
                        <div key={index} className="flex items-center space-x-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                          <Calendar className="h-4 w-4 text-purple-500" />
                          <span className="text-sm font-medium">{result.domain}</span>
                          <span className="text-xs text-purple-600 bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded">
                            Domain expired
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {(results.summary.unregistered ?? 0) > 0 && (
                <>
                  <div 
                    className="flex items-center justify-between p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg cursor-pointer hover:bg-yellow-500/20 transition-colors"
                    onClick={() => toggleSummaryCategory('unregistered')}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-yellow-600">Unregistered Domains</span>
                      {expandedSummaryCategories.has('unregistered') ? (
                        <ChevronUp className="h-4 w-4 text-yellow-600" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-yellow-600" />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard('unregistered');
                        }}
                        className="p-1 hover:bg-yellow-600/20 rounded transition-colors"
                        title="Copy domains to clipboard"
                      >
                        {copiedCategory === 'unregistered' ? (
                          <Check className="h-4 w-4 text-yellow-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-yellow-600" />
                        )}
                      </button>
                      <span className="text-xl font-bold text-yellow-600">{results.summary.unregistered}</span>
                    </div>
                  </div>
                  {expandedSummaryCategories.has('unregistered') && (
                    <div className="ml-4 space-y-2">
                      {getDomainsByCategory('unregistered').map((result, index) => (
                        <div key={index} className="flex items-center space-x-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm font-medium">{result.domain}</span>
                          <span className="text-xs text-yellow-600 bg-yellow-100 dark:bg-yellow-800 px-2 py-1 rounded">
                            Domain is unregistered
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {(results.summary.notInCloudflare ?? 0) > 0 && (
                <>
                  <div 
                    className="flex items-center justify-between p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg cursor-pointer hover:bg-indigo-500/20 transition-colors"
                    onClick={() => toggleSummaryCategory('not-in-cf')}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-indigo-600">Not Found in Cloudflare</span>
                      {expandedSummaryCategories.has('not-in-cf') ? (
                        <ChevronUp className="h-4 w-4 text-indigo-600" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-indigo-600" />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard('not-in-cf');
                        }}
                        className="p-1 hover:bg-indigo-600/20 rounded transition-colors"
                        title="Copy domains to clipboard"
                      >
                        {copiedCategory === 'not-in-cf' ? (
                          <Check className="h-4 w-4 text-indigo-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-indigo-600" />
                        )}
                      </button>
                      <span className="text-xl font-bold text-indigo-600">{results.summary.notInCloudflare}</span>
                    </div>
                  </div>
                  {expandedSummaryCategories.has('not-in-cf') && (
                    <div className="ml-4 space-y-2">
                      {getDomainsByCategory('not-in-cf').map((result, index) => (
                        <div key={index} className="flex items-center space-x-3 p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded">
                          <Cloud className="h-4 w-4 text-indigo-500" />
                          <span className="text-sm font-medium">{result.domain}</span>
                          <span className="text-xs text-indigo-600 bg-indigo-100 dark:bg-indigo-800 px-2 py-1 rounded">
                            Not in Cloudflare
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              <div 
                className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-lg cursor-pointer hover:bg-red-500/20 transition-colors"
                onClick={() => toggleSummaryCategory('failed')}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-red-600">Failed</span>
                  {expandedSummaryCategories.has('failed') ? (
                    <ChevronUp className="h-4 w-4 text-red-600" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-red-600" />
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard('failed');
                    }}
                    className="p-1 hover:bg-red-600/20 rounded transition-colors"
                    title="Copy domains to clipboard"
                  >
                    {copiedCategory === 'failed' ? (
                      <Check className="h-4 w-4 text-red-600" />
                    ) : (
                      <Copy className="h-4 w-4 text-red-600" />
                    )}
                  </button>
                  <span className="text-xl font-bold text-red-600">{results.summary.failed}</span>
                </div>
              </div>
              {expandedSummaryCategories.has('failed') && results.summary.failed > 0 && (
                <div className="ml-4 space-y-2">
                  {getDomainsByCategory('failed').map((result, index) => (
                    <div key={index} className="flex items-center space-x-3 p-2 bg-red-50 dark:bg-red-900/20 rounded">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-sm font-medium">{result.domain}</span>
                      {result.registrar && (
                        <span className="text-xs text-red-600 bg-red-100 dark:bg-red-800 px-2 py-1 rounded">
                          {result.registrar}
                        </span>
                      )}
                      {result.error && (
                        <span className="text-xs text-red-500 truncate max-w-xs" title={result.error}>
                          {result.error}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="card-modern p-4">
          <h3 className="font-medium mb-2 flex items-center space-x-2">
            <Globe className="h-4 w-4 text-primary" />
            <span>Live Processing Steps</span>
          </h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>• Check registrar using external API</p>
            <p>• Verify domain is owned by Alphoric and not expired</p>
            <p>• Check if domain exists in Cloudflare</p>
            <p>• Fetch nameservers from failsafe database</p>
            <p>• Skip update if domain status is already "active"</p>
            <p>• Update nameservers at registrar-specific API if needed</p>
            <p>• Show real-time responses for each step</p>
          </div>
        </div>
        <div className="card-modern p-4 bg-blue-500/10 border border-blue-500/20">
          <p className="text-sm text-blue-600 dark:text-blue-400">
            <strong>Note:</strong> Domains are processed sequentially to show live updates. API keys are optional and will fall back to environment variables when available.
          </p>
        </div>
      </div>
    </div>
  );
}; 